import React from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Phone, Mail, MessageSquare, FileText, Link2, CheckCircle2 } from 'lucide-react';
import { formatDate } from '../../utils/dateFormat';

const LABEL = { 1: 'Initial Contact', 2: 'Contact 2', 3: 'Contact 3', 4: 'Contact 4' };

const DueBadge = ({ days, date, testId }) => (
  days > 0
    ? <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200" data-testid={testId}>Overdue {days} day{days === 1 ? '' : 's'} · {formatDate(date)}</Badge>
    : <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200" data-testid={testId}>Due today</Badge>
);

export const MyOutreachQueue = ({ items, ctx }) => {
  if (!items.length) {
    return (
      <div className="py-14 text-center" data-testid="rt-myqueue-empty">
        <CheckCircle2 className="w-10 h-10 mx-auto text-green-600 mb-2" />
        <p className="font-medium">You're all caught up</p>
        <p className="text-sm text-muted-foreground">No contacts assigned to you are due today or overdue.</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto" data-testid="rt-myqueue-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted">
            {['Participant', 'Tab', 'Contact', 'Status', 'Due', 'Actions'].map((h) => <th key={h} className="text-left py-2.5 px-3 text-xs font-semibold uppercase text-muted-foreground whitespace-nowrap">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {items.map(({ record: r, n, due_date: due, days_overdue: days, tab_name: tab }) => {
            const key = `${r.id}-${n}`;
            const linked = !!r.participant_id;
            const status = (r.contacts.find((c) => c.n === n) || {}).status;
            return (
              <tr key={key} className="border-b border-border last:border-0 hover:bg-muted/30" data-testid={`rt-myqueue-row-${key}`}>
                <td className="py-2 px-3 font-medium whitespace-nowrap">
                  {r.first_name} {r.last_name}
                  {linked
                    ? <Link2 className="inline w-3.5 h-3.5 ml-1 text-primary" />
                    : <Button variant="link" size="sm" className="h-auto p-0 ml-2 text-xs" onClick={() => ctx.onLink(r)} data-testid={`rt-myqueue-link-${key}`}>Link to profile</Button>}
                </td>
                <td className="py-2 px-3 whitespace-nowrap">{tab.replace('SNAP E&T Program - ', '')}</td>
                <td className="py-2 px-3 whitespace-nowrap">{LABEL[n]}</td>
                <td className="py-2 px-3 whitespace-nowrap">{status || 'No status'}</td>
                <td className="py-2 px-3 whitespace-nowrap"><DueBadge days={days} date={due} testId={`rt-myqueue-due-${key}`} /></td>
                <td className="py-1 px-2 whitespace-nowrap">
                  <Button size="sm" variant="outline" className="h-7 mr-1" onClick={() => ctx.onEditContact(r, n)} data-testid={`rt-myqueue-outcome-${key}`}>Record Outcome</Button>
                  {n >= 2 && <Button size="icon" variant="ghost" className="h-7 w-7" title="Call" disabled={!linked} onClick={() => ctx.onAction(r, n, 'call')} data-testid={`rt-myqueue-call-${key}`}><Phone className="w-3.5 h-3.5" /></Button>}
                  <Button size="icon" variant="ghost" className="h-7 w-7" title="Email" disabled={!linked} onClick={() => ctx.onAction(r, n, 'email')} data-testid={`rt-myqueue-email-${key}`}><Mail className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" title="Text" disabled={!linked} onClick={() => ctx.onAction(r, n, 'sms')} data-testid={`rt-myqueue-sms-${key}`}><MessageSquare className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" title="Add Case Note" disabled={!linked} onClick={() => ctx.onAction(r, n, 'note')} data-testid={`rt-myqueue-note-${key}`}><FileText className="w-3.5 h-3.5" /></Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
